import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiArrowRight,
  FiBookOpen,
  FiCheckCircle,
  FiChevronDown,
  FiExternalLink,
  FiFileText,
  FiHelpCircle,
  FiRefreshCw,
  FiSearch,
  FiX,
} from "react-icons/fi";
import { acknowledgeLearningCourse, getLearning, getLearningCourse } from "../api/hrmApi";
import { normalizeLearning, normalizeLearningCourseResponse } from "../api/hrmNormalizers";
import ComponentLoader from "../components/ComponentLoader";
import ScreenHeader from "./ScreenHeader";
import TrainingTests from "./TrainingTests";

const statusFilters = ["All", "Pending acknowledgement", "Assigned", "Acknowledged"];
const categoryOrder = [
  "antrazal foundation fle",
  "antrazal foundation (fle)",
  "antrazal foundations",
  "foundations",
  "foundation",
  "training plan",
  "training plan extras",
  "data model",
  "automation",
  "security",
  "lwc",
  "async",
];
const categoryMaxTimeFallbacks = [
  { keys: ["antrazal foundation fle", "antrazal foundation (fle)", "antrazal foundations", "foundations", "foundation"], hours: 8 },
  { keys: ["training plan extras"], hours: 8 },
  { keys: ["training plan"], hours: 120 },
  { keys: ["data model", "data-model"], hours: 8 },
  { keys: ["automation", "automations"], hours: 24 },
  { keys: ["security"], hours: 24 },
  { keys: ["lwc"], hours: 24 },
  { keys: ["async apex", "async"], hours: 24 },
];

const getCourseKey = (course = {}) => String(course.assignmentId || course.id || "");

const getPendingAcknowledgementCourses = (courseList = []) =>
  courseList.filter((course) => course.assignmentId && course.requiresAcknowledgement && !course.acknowledged);

const normalizeCategoryName = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");

const getCategoryOrderIndex = (category = "") => {
  const normalizedCategory = normalizeCategoryName(category);
  const exactIndex = categoryOrder.indexOf(normalizedCategory);
  if (exactIndex >= 0) {
    return exactIndex;
  }

  const partialIndex = categoryOrder.findIndex((orderedCategory) => normalizedCategory.includes(orderedCategory));
  return partialIndex >= 0 ? partialIndex : Number.MAX_SAFE_INTEGER;
};

const getCourseMaxTimeHours = (course = {}) => {
  const maxTimeHours = Number(course.maxTimeHours || course.durationHours || 0);
  if (Number.isFinite(maxTimeHours) && maxTimeHours > 0) {
    return maxTimeHours;
  }
  const durationMinutes = Number(course.durationMinutes || 0);
  return Number.isFinite(durationMinutes) && durationMinutes > 0 ? durationMinutes / 60 : 0;
};

const getCategoryMaxTimeHours = (category = "", categoryCourses = []) => {
  const configuredHours = categoryCourses
    .map(getCourseMaxTimeHours)
    .filter((hours) => Number.isFinite(hours) && hours > 0);
  if (configuredHours.length) {
    return Math.max(...configuredHours);
  }

  const normalizedCategory = normalizeCategoryName(category);
  const fallback = categoryMaxTimeFallbacks.find(({ keys }) =>
    keys.some((key) => normalizedCategory === normalizeCategoryName(key) || normalizedCategory.includes(normalizeCategoryName(key)))
  );
  return fallback?.hours || 0;
};

const formatTrainingMaxTime = (hours = 0) => {
  const numericHours = Number(hours);
  if (!Number.isFinite(numericHours) || numericHours <= 0) {
    return "Max time not set";
  }
  const formattedHours = numericHours % 1 === 0 ? String(numericHours) : numericHours.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
  return `${formattedHours}h max`;
};

const getStatusTone = (course = {}) => {
  if (course.acknowledged || course.status === "Acknowledged") {
    return "green";
  }
  if (course.requiresAcknowledgement) {
    return "amber";
  }
  return "blue";
};

const courseMatchesStatus = (course, status) => {
  if (status === "All") {
    return true;
  }
  if (status === "Pending acknowledgement") {
    return course.requiresAcknowledgement && !course.acknowledged;
  }
  if (status === "Acknowledged") {
    return course.acknowledged || course.status === "Acknowledged";
  }
  return course.status === status;
};

const stripUnsafeTrainingHtml = (html = "") => {
  if (!html || typeof window === "undefined" || !window.DOMParser) {
    return html;
  }

  const documentBody = new window.DOMParser().parseFromString(html, "text/html").body;
  documentBody.querySelectorAll("script,style,iframe,object,embed").forEach((node) => node.remove());
  documentBody.querySelectorAll("*").forEach((node) => {
    Array.from(node.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value || "";
      if (name.startsWith("on") || /javascript:/i.test(value)) {
        node.removeAttribute(attribute.name);
      }
    });
  });

  return documentBody.innerHTML;
};

const extractTrainingContentLines = (html = "") => {
  if (!html) {
    return [];
  }

  const htmlWithBreaks = String(html)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, "\n");
  const text =
    typeof window !== "undefined" && window.DOMParser
      ? new window.DOMParser().parseFromString(htmlWithBreaks, "text/html").body.textContent || ""
      : htmlWithBreaks.replace(/<[^>]+>/g, " ");

  return text
    .split(/\n+/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);
};

const formatTrainingNoteDate = (value = "") => {
  const match = value.match(/^(\d{1,2})-([A-Za-z]{3,9})-(\d{2}|\d{4})$/);
  if (!match) {
    return value;
  }

  const [, day, month, year] = match;
  const monthNames = {
    apr: "Apr",
    aug: "Aug",
    dec: "Dec",
    feb: "Feb",
    jan: "Jan",
    jul: "Jul",
    jun: "Jun",
    mar: "Mar",
    may: "May",
    nov: "Nov",
    oct: "Oct",
    sep: "Sep",
  };
  const monthName = monthNames[month.toLowerCase().slice(0, 3)] || month;
  const numericYear = Number(year);
  const fullYear = year.length === 2 ? 2000 + numericYear : numericYear;

  return `${String(Number(day)).padStart(2, "0")} ${monthName} ${fullYear}`;
};

const parseDatedTrainingNotes = (html = "") => {
  const lines = extractTrainingContentLines(html);
  const notes = [];
  let currentNote = null;

  lines.forEach((line) => {
    const dateMatch = line.match(/^ON\s+(\d{1,2}-[A-Za-z]{3,9}-\d{2,4})$/i);
    if (dateMatch) {
      if (currentNote?.lines.length) {
        notes.push({
          id: `${currentNote.rawDate}-${notes.length}`,
          date: formatTrainingNoteDate(currentNote.rawDate),
          body: currentNote.lines.join(" "),
        });
      }
      currentNote = {
        rawDate: dateMatch[1],
        lines: [],
      };
      return;
    }

    if (currentNote) {
      currentNote.lines.push(line);
    }
  });

  if (currentNote?.lines.length) {
    notes.push({
      id: `${currentNote.rawDate}-${notes.length}`,
      date: formatTrainingNoteDate(currentNote.rawDate),
      body: currentNote.lines.join(" "),
    });
  }

  return notes;
};

const markAcknowledged = (course = {}, acknowledgedAt = "") => ({
  ...course,
  acknowledged: true,
  acknowledgedAt: acknowledgedAt || course.acknowledgedAt || "Completed",
  completedDate: course.completedDate || acknowledgedAt || "",
  hasProof: true,
  progress: 100,
  status: "Acknowledged",
});

const LearningScreen = ({ onAction, viewUserId = "" }) => {
  const [courses, setCourses] = useState([]);
  const [summary, setSummary] = useState({
    acknowledgedCount: 0,
    completedCount: 0,
    courseCount: 0,
    hoursLeft: 0,
    pendingAcknowledgementCount: 0,
  });
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [expandedCourseId, setExpandedCourseId] = useState("");
  const [expandedCategories, setExpandedCategories] = useState(() => new Set());
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [moduleAcknowledging, setModuleAcknowledging] = useState(false);
  const [moduleAckDialog, setModuleAckDialog] = useState(null);
  const [moduleAckError, setModuleAckError] = useState("");
  const [moduleAckProgress, setModuleAckProgress] = useState({ completed: 0, total: 0 });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [acknowledgementForm, setAcknowledgementForm] = useState({
    employeeComments: "",
    proof: "",
    proofUrl: "",
  });
  const [moduleAckForm, setModuleAckForm] = useState({
    employeeComments: "",
    proof: "",
    proofUrl: "",
  });
  const acknowledgedCourseIdsRef = useRef(new Set());
  const courseDetailRequestRef = useRef(0);
  const selectedCourseIdRef = useRef("");

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    selectedCourseIdRef.current = selectedCourseId;
  }, [selectedCourseId]);

  useEffect(() => {
    setAcknowledgementForm({
      employeeComments: selectedCourse?.employeeComments || "",
      proof: selectedCourse?.proof || "",
      proofUrl: selectedCourse?.proofUrl || "",
    });
  }, [selectedCourse?.assignmentId, selectedCourse?.employeeComments, selectedCourse?.proof, selectedCourse?.proofUrl]);

  const applyLocalAcknowledgements = useCallback((nextCourses) =>
    nextCourses.map((course) =>
      acknowledgedCourseIdsRef.current.has(getCourseKey(course)) ? markAcknowledged(course) : course
    ), []);

  const loadCourseDetail = useCallback(async (course) => {
    const courseKey = getCourseKey(course);
    if (!courseKey) {
      courseDetailRequestRef.current += 1;
      setSelectedCourse(null);
      return;
    }

    const requestId = ++courseDetailRequestRef.current;
    setSelectedCourse(course);
    setSelectedCourseId(courseKey);
    setDetailLoading(true);
    setError("");
    try {
      const payload = await getLearningCourse(viewUserId, course.assignmentId || course.id);
      if (requestId !== courseDetailRequestRef.current) {
        return;
      }
      const normalizedCourse = normalizeLearningCourseResponse(payload, course);
      const nextCourse = acknowledgedCourseIdsRef.current.has(getCourseKey(normalizedCourse))
        ? markAcknowledged(normalizedCourse)
        : normalizedCourse;
      setSelectedCourse(nextCourse);
      setCourses((currentCourses) =>
        currentCourses.map((currentCourse) =>
          getCourseKey(currentCourse) === getCourseKey(nextCourse) ? nextCourse : currentCourse
        )
      );
    } catch (loadError) {
      if (requestId === courseDetailRequestRef.current) {
        setError(loadError.message || "Training detail could not be loaded.");
      }
    } finally {
      if (requestId === courseDetailRequestRef.current) {
        setDetailLoading(false);
      }
    }
  }, [viewUserId]);

  const loadTraining = useCallback(async () => {
    setLoading(true);
    setError("");
    const apiStatus = ["Assigned", "Acknowledged"].includes(statusFilter) ? statusFilter : "";
    try {
      const payload = await getLearning(viewUserId, {
        page: 1,
        pageSize: 500,
        q: debouncedSearchTerm,
        status: apiStatus,
      });
      const normalizedLearning = normalizeLearning(payload);
      const nextCourses = applyLocalAcknowledgements(normalizedLearning.courses);
      const acknowledgedCount = nextCourses.filter((course) => course.acknowledged).length;
      const pendingAcknowledgementCount = nextCourses.filter((course) => course.requiresAcknowledgement && !course.acknowledged).length;
      setCourses(nextCourses);
      setSummary({
        ...normalizedLearning.summary,
        acknowledgedCount,
        pendingAcknowledgementCount,
      });
      const selectedStillExists = nextCourses.find((course) => getCourseKey(course) === selectedCourseIdRef.current);
      const nextSelectedCourse = selectedStillExists || nextCourses[0] || null;
      if (nextSelectedCourse) {
        loadCourseDetail(nextSelectedCourse);
      } else {
        setSelectedCourseId("");
        setSelectedCourse(null);
      }
    } catch (loadError) {
      setCourses([]);
      setSelectedCourseId("");
      setSelectedCourse(null);
      setError(loadError.message || "Training assignments could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [applyLocalAcknowledgements, debouncedSearchTerm, loadCourseDetail, statusFilter, viewUserId]);

  useEffect(() => {
    loadTraining();
  }, [loadTraining]);

  const visibleCourses = useMemo(
    () => courses.filter((course) => courseMatchesStatus(course, statusFilter)),
    [courses, statusFilter]
  );

  useEffect(() => {
    if (loading || !visibleCourses.length) {
      return;
    }
    if (!visibleCourses.some((course) => getCourseKey(course) === selectedCourseId)) {
      loadCourseDetail(visibleCourses[0]);
    }
  }, [loadCourseDetail, loading, selectedCourseId, visibleCourses]);

  useEffect(() => {
    if (expandedCourseId && !visibleCourses.some((course) => getCourseKey(course) === expandedCourseId)) {
      setExpandedCourseId("");
    }
  }, [expandedCourseId, visibleCourses]);

  const groupedCourses = useMemo(() => {
    const grouped = new Map();
    visibleCourses.forEach((course) => {
      const category = course.category || "General";
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category).push(course);
    });

    return Array.from(grouped.entries())
      .sort(([firstCategory], [secondCategory]) => {
        const firstIndex = getCategoryOrderIndex(firstCategory);
        const secondIndex = getCategoryOrderIndex(secondCategory);
        if (firstIndex !== secondIndex) {
          return firstIndex - secondIndex;
        }
        return firstCategory.localeCompare(secondCategory);
      })
      .map(([category, categoryCourses]) => ({
        category,
        courses: categoryCourses,
        maxTimeHours: getCategoryMaxTimeHours(category, categoryCourses),
        pendingCount: categoryCourses.filter((course) => course.requiresAcknowledgement && !course.acknowledged).length,
      }));
  }, [visibleCourses]);
  const firstPendingGroupIndex = groupedCourses.findIndex((group) => group.pendingCount > 0);

  const pendingCount = summary.pendingAcknowledgementCount;
  const completionPercent = summary.courseCount
    ? Math.round((summary.acknowledgedCount / summary.courseCount) * 100)
    : 0;
  const sanitizedContent = useMemo(
    () => stripUnsafeTrainingHtml(selectedCourse?.content || ""),
    [selectedCourse?.content]
  );
  const trainingNotes = useMemo(
    () => parseDatedTrainingNotes(selectedCourse?.content || ""),
    [selectedCourse?.content]
  );
  const acknowledgementProofReady = Boolean(acknowledgementForm.proof.trim() || acknowledgementForm.proofUrl.trim());
  const moduleAckProofReady = Boolean(moduleAckForm.proof.trim() || moduleAckForm.proofUrl.trim());
  const visiblePendingCourses = useMemo(() => getPendingAcknowledgementCourses(visibleCourses), [visibleCourses]);
  const featuredCourse = useMemo(
    () => visiblePendingCourses[0] || visibleCourses.find((course) => !course.acknowledged) || visibleCourses[0] || null,
    [visibleCourses, visiblePendingCourses]
  );

  const openFeaturedCourse = () => {
    if (!featuredCourse) {
      return;
    }
    const category = featuredCourse.category || "General";
    setExpandedCategories((currentCategories) => new Set([...currentCategories, category]));
    setExpandedCourseId(getCourseKey(featuredCourse));
    loadCourseDetail(featuredCourse);
  };

  const toggleCategory = (category) => {
    setExpandedCategories((currentCategories) => {
      const nextCategories = new Set(currentCategories);
      if (nextCategories.has(category)) {
        nextCategories.delete(category);
      } else {
        nextCategories.add(category);
      }
      return nextCategories;
    });
  };

  const handleAcknowledge = async () => {
    if (!selectedCourse?.assignmentId) {
      return;
    }

    const proof = acknowledgementForm.proof.trim();
    const proofUrl = acknowledgementForm.proofUrl.trim();
    const employeeComments = acknowledgementForm.employeeComments.trim();
    if (!proof && !proofUrl) {
      setError("Training proof is required to acknowledge the assignment.");
      return;
    }
    if (proofUrl && !/^https?:\/\/\S+/i.test(proofUrl)) {
      setError("Proof link must start with http:// or https://.");
      return;
    }

    setAcknowledging(true);
    setError("");
    setMessage("");
    try {
      const payload = await acknowledgeLearningCourse(viewUserId, selectedCourse.assignmentId, {
        proof,
        proofUrl,
        employeeComments,
      });
      const acknowledgementFallback = markAcknowledged({
        ...selectedCourse,
        employeeComments,
        proof,
        proofUrl,
      });
      const acknowledgedCourse = markAcknowledged(
        normalizeLearningCourseResponse(payload, acknowledgementFallback),
        new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
      );
      acknowledgedCourseIdsRef.current.add(getCourseKey(acknowledgedCourse));
      setSelectedCourse(acknowledgedCourse);
      setCourses((currentCourses) =>
        currentCourses.map((course) =>
          getCourseKey(course) === getCourseKey(acknowledgedCourse) ? acknowledgedCourse : course
        )
      );
      setSummary((currentSummary) => ({
        ...currentSummary,
        acknowledgedCount: Math.min((currentSummary.acknowledgedCount || 0) + 1, currentSummary.courseCount || 0),
        pendingAcknowledgementCount: Math.max((currentSummary.pendingAcknowledgementCount || 0) - 1, 0),
      }));
      setMessage("Training acknowledgement saved.");
    } catch (ackError) {
      setError(ackError.message || "Training acknowledgement could not be saved.");
    } finally {
      setAcknowledging(false);
    }
  };

  const openModuleAcknowledgement = (title, courseList) => {
    const pendingCourses = getPendingAcknowledgementCourses(courseList);
    setError("");
    setMessage("");
    if (!pendingCourses.length) {
      setMessage(`${title} is already acknowledged.`);
      return;
    }

    setModuleAckDialog({
      title,
      courses: pendingCourses,
    });
    setModuleAckForm({
      employeeComments: "",
      proof: "",
      proofUrl: "",
    });
    setModuleAckError("");
    setModuleAckProgress({ completed: 0, total: pendingCourses.length });
  };

  const closeModuleAcknowledgement = () => {
    if (moduleAcknowledging) {
      return;
    }
    setModuleAckDialog(null);
    setModuleAckError("");
    setModuleAckProgress({ completed: 0, total: 0 });
  };

  const handleAcknowledgeModule = async () => {
    if (!moduleAckDialog?.courses?.length) {
      return;
    }

    const proof = moduleAckForm.proof.trim();
    const proofUrl = moduleAckForm.proofUrl.trim();
    const employeeComments = moduleAckForm.employeeComments.trim();
    if (!proof && !proofUrl) {
      setModuleAckError("Training proof is required to acknowledge the module.");
      return;
    }
    if (proofUrl && !/^https?:\/\/\S+/i.test(proofUrl)) {
      setModuleAckError("Proof link must start with http:// or https://.");
      return;
    }

    const pendingCourses = getPendingAcknowledgementCourses(moduleAckDialog.courses);
    const acknowledgedCourses = [];
    const failedCourses = [];
    const acknowledgedAt = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

    setModuleAcknowledging(true);
    setError("");
    setModuleAckError("");
    setMessage("");
    setModuleAckProgress({ completed: 0, total: pendingCourses.length });

    for (const course of pendingCourses) {
      try {
        const payload = await acknowledgeLearningCourse(viewUserId, course.assignmentId, {
          proof,
          proofUrl,
          employeeComments,
        });
        const fallbackCourse = markAcknowledged({
          ...course,
          employeeComments,
          proof,
          proofUrl,
        });
        const acknowledgedCourse = markAcknowledged(
          normalizeLearningCourseResponse(payload, fallbackCourse),
          acknowledgedAt
        );
        acknowledgedCourseIdsRef.current.add(getCourseKey(acknowledgedCourse));
        acknowledgedCourses.push(acknowledgedCourse);
      } catch (ackError) {
        failedCourses.push({
          course,
          message: ackError.message || "Acknowledgement failed.",
        });
      } finally {
        setModuleAckProgress((currentProgress) => ({
          ...currentProgress,
          completed: Math.min(currentProgress.completed + 1, currentProgress.total),
        }));
      }
    }

    if (acknowledgedCourses.length) {
      const acknowledgedByKey = new Map(acknowledgedCourses.map((course) => [getCourseKey(course), course]));
      setCourses((currentCourses) => currentCourses.map((course) => acknowledgedByKey.get(getCourseKey(course)) || course));
      setSelectedCourse((currentCourse) => acknowledgedByKey.get(getCourseKey(currentCourse)) || currentCourse);
      setSummary((currentSummary) => ({
        ...currentSummary,
        acknowledgedCount: Math.min((currentSummary.acknowledgedCount || 0) + acknowledgedCourses.length, currentSummary.courseCount || 0),
        pendingAcknowledgementCount: Math.max((currentSummary.pendingAcknowledgementCount || 0) - acknowledgedCourses.length, 0),
      }));
    }

    setModuleAcknowledging(false);

    if (failedCourses.length) {
      setModuleAckError(
        `${failedCourses.length} training ${failedCourses.length === 1 ? "assignment" : "assignments"} could not be acknowledged. ${failedCourses[0].course.title}: ${failedCourses[0].message}`
      );
      return;
    }

    setModuleAckDialog(null);
    setModuleAckError("");
    setMessage(`${acknowledgedCourses.length} training ${acknowledgedCourses.length === 1 ? "assignment" : "assignments"} acknowledged.`);
    loadTraining();
  };

  const renderSelectedCourseDetail = () => (
    <section className="new-hrm-learning-detail new-hrm-loader-host">
      <ComponentLoader active={detailLoading || acknowledging} label={acknowledging ? "Saving acknowledgement" : "Loading module"} />
      {selectedCourse ? (
        <>
          <div className="new-hrm-learning-detail__meta">
            <span><small>Assigned</small><strong>{selectedCourse.assignedDate || "Not set"}</strong></span>
            <span><small>Completed</small><strong>{selectedCourse.completedDate || "Not completed"}</strong></span>
            <span><small>Mentor Review</small><strong>{selectedCourse.mentorApprovalStatus || "Pending Review"}</strong></span>
            <span><small>Proof</small><strong>{selectedCourse.hasProof ? "Submitted" : "Required"}</strong></span>
            <span><small>Max Time</small><strong>{formatTrainingMaxTime(getCourseMaxTimeHours(selectedCourse))}</strong></span>
            <span><small>Due</small><strong>{selectedCourse.dueDate || "No due date"}</strong></span>
            <span><small>Progress</small><strong>{selectedCourse.progress || 0}%</strong></span>
          </div>

          {selectedCourse.description && (
            <section className="new-hrm-learning-detail__summary">
              <h3>Overview</h3>
              <p>{selectedCourse.description}</p>
            </section>
          )}

          <section className="new-hrm-learning-detail__content">
            <h3>{trainingNotes.length ? "Comments" : "Training Detail"}</h3>
            {trainingNotes.length ? (
              <div className="new-hrm-learning-notes-timeline">
                {trainingNotes.map((note) => (
                  <article key={note.id}>
                    <time>{note.date}</time>
                    <p>{note.body}</p>
                  </article>
                ))}
              </div>
            ) : sanitizedContent ? (
              <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
            ) : (
              <p>No training content was returned for this module.</p>
            )}
          </section>

          {selectedCourse.requiresAcknowledgement && !selectedCourse.acknowledged ? (
            <section className="new-hrm-learning-proof">
              <div>
                <h3>Proof for Mentor Review</h3>
                <p>Add proof text or a proof link before acknowledging this assignment.</p>
              </div>
              <label>
                <span>Proof</span>
                <textarea
                  rows={4}
                  value={acknowledgementForm.proof}
                  onChange={(event) => setAcknowledgementForm((current) => ({ ...current, proof: event.target.value }))}
                  placeholder="Summarize the work completed or paste the proof notes."
                />
              </label>
              <label>
                <span>Proof URL</span>
                <input
                  type="url"
                  value={acknowledgementForm.proofUrl}
                  onChange={(event) => setAcknowledgementForm((current) => ({ ...current, proofUrl: event.target.value }))}
                  placeholder="https://..."
                />
              </label>
              <label className="new-hrm-learning-proof__full">
                <span>Comments for Mentor</span>
                <textarea
                  rows={3}
                  value={acknowledgementForm.employeeComments}
                  onChange={(event) => setAcknowledgementForm((current) => ({ ...current, employeeComments: event.target.value }))}
                  placeholder="Optional notes for mentor review."
                />
              </label>
            </section>
          ) : selectedCourse.hasProof ? (
            <section className="new-hrm-learning-proof new-hrm-learning-proof--readonly">
              <div>
                <h3>Proof Submitted</h3>
                <p>{selectedCourse.mentorApprovalStatus || "Pending Review"}</p>
              </div>
              {selectedCourse.proof && (
                <article>
                  <span>Proof</span>
                  <p>{selectedCourse.proof}</p>
                </article>
              )}
              {selectedCourse.proofUrl && (
                <article>
                  <span>Proof URL</span>
                  <a href={selectedCourse.proofUrl} target="_blank" rel="noreferrer">{selectedCourse.proofUrl}</a>
                </article>
              )}
              {selectedCourse.employeeComments && (
                <article className="new-hrm-learning-proof__full">
                  <span>Employee Comments</span>
                  <p>{selectedCourse.employeeComments}</p>
                </article>
              )}
            </section>
          ) : null}

          <div className="new-hrm-learning-detail__actions">
            <div>
              {selectedCourse.courseUrl && (
                <a className="new-hrm-secondary-button" href={selectedCourse.courseUrl} target="_blank" rel="noreferrer">
                  <FiExternalLink /> Open Course
                </a>
              )}
              {selectedCourse.documentUrl && (
                <a className="new-hrm-secondary-button" href={selectedCourse.documentUrl} target="_blank" rel="noreferrer">
                  <FiFileText /> Open Document
                </a>
              )}
            </div>
            {selectedCourse.requiresAcknowledgement && !selectedCourse.acknowledged ? (
              <button className="new-hrm-primary-button" type="button" onClick={handleAcknowledge} disabled={acknowledging || !acknowledgementProofReady}>
                <FiCheckCircle /> Acknowledge Training
              </button>
            ) : (
              <strong>
                <FiCheckCircle /> Acknowledgement complete
              </strong>
            )}
          </div>
        </>
      ) : (
        <p className="new-hrm-empty-state">Select a training module to review the details.</p>
      )}
    </section>
  );

  return (
    <section className="new-hrm-screen new-hrm-learning-screen new-hrm-learning-screen--reference">
      <ScreenHeader
        eyebrow="Enablement"
        title="Training"
        description="A focused path through assigned learning and evidence."
        actions={
          <>
            <button className="new-hrm-icon-button" type="button" onClick={loadTraining} disabled={loading} aria-label="Refresh training" title="Refresh training">
              <FiRefreshCw aria-hidden="true" />
            </button>
            <button className="new-hrm-secondary-button" type="button" onClick={() => onAction("hr")}>
              <FiHelpCircle aria-hidden="true" />
              <span>Ask HR</span>
            </button>
          </>
        }
      />

      <section className="new-hrm-learning-overview" aria-labelledby="learning-overview-title">
        <div className="new-hrm-learning-overview__copy">
          <span className="new-hrm-reference-kicker">Antrazal Academy</span>
          <h2 id="learning-overview-title">Build the skills clients trust.</h2>
          <p>Continue the next assignment, then move through your learning path without LMS clutter.</p>
          {featuredCourse ? (
            <div className="new-hrm-learning-resume">
              <span className={`new-hrm-learning-resume__icon new-hrm-learning-status--${getStatusTone(featuredCourse)}`}>
                {featuredCourse.acknowledged ? <FiCheckCircle aria-hidden="true" /> : <FiBookOpen aria-hidden="true" />}
              </span>
              <span>
                <small>{featuredCourse.category || "Current focus"}</small>
                <strong>{featuredCourse.title}</strong>
                <em>{featuredCourse.provider || "Antrazal Training"} · {featuredCourse.progress || 0}% complete</em>
              </span>
              <button className="new-hrm-secondary-button" type="button" onClick={openFeaturedCourse}>
                <span>{featuredCourse.progress ? "Resume" : "Open"}</span>
                <FiArrowRight aria-hidden="true" />
              </button>
            </div>
          ) : (
            <p className="new-hrm-learning-overview__empty">No active training assignment is available.</p>
          )}
        </div>
        <figure className="new-hrm-learning-overview__visual">
          <img src="/assets/hrm/training-2173.webp" alt="Antrazal employee working across a multi-screen setup" />
          <figcaption>
            <strong>{completionPercent}%</strong>
            <span>Learning complete</span>
          </figcaption>
        </figure>
      </section>

      {!!groupedCourses.length && (
        <section className="new-hrm-learning-path" aria-labelledby="learning-path-title">
          <header>
            <h2 id="learning-path-title">Your learning path</h2>
            <p>{summary.acknowledgedCount} acknowledged · {pendingCount} awaiting action</p>
          </header>
          <ol>
            {groupedCourses.slice(0, 4).map((group, index) => (
              <li className={group.pendingCount ? (index === firstPendingGroupIndex ? "is-current" : "") : "is-complete"} key={group.category}>
                <span>{group.pendingCount ? index + 1 : <FiCheckCircle aria-hidden="true" />}</span>
                <small>{group.pendingCount ? (index === firstPendingGroupIndex ? "Current focus" : "Next") : "Complete"}</small>
                <strong>{group.category}</strong>
                <em>{group.courses.length} {group.courses.length === 1 ? "module" : "modules"}</em>
              </li>
            ))}
          </ol>
        </section>
      )}

      <section className="new-hrm-panel new-hrm-learning-workspace new-hrm-loader-host">
        <ComponentLoader active={loading} label="Loading training" />
        <div className="new-hrm-learning-toolbar">
          <div className="new-hrm-learning-workbar__status">
            <span className="new-hrm-learning-workbar__icon">
              <FiBookOpen aria-hidden="true" />
            </span>
            <div>
              <strong>{pendingCount ? `${pendingCount} awaiting acknowledgement` : "Training is up to date"}</strong>
              <small>{summary.courseCount} assigned · {summary.acknowledgedCount} acknowledged · {summary.hoursLeft}h left</small>
            </div>
            <em>{completionPercent}% complete</em>
          </div>
          <div className="new-hrm-learning-filters">
            <label>
              <FiSearch aria-hidden="true" />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search training"
              />
            </label>
            <div className="new-hrm-learning-segments" role="group" aria-label="Training status">
              {statusFilters.map((status) => (
                <button
                  className={statusFilter === status ? "new-hrm-learning-segment new-hrm-learning-segment--active" : "new-hrm-learning-segment"}
                  key={status}
                  type="button"
                  aria-pressed={statusFilter === status}
                  onClick={() => setStatusFilter(status)}
                >
                  {status === "Pending acknowledgement" ? "Pending" : status}
                </button>
              ))}
            </div>
          </div>
          <button
            className="new-hrm-primary-button"
            type="button"
            onClick={() => openModuleAcknowledgement("Shown training", visibleCourses)}
            disabled={!visiblePendingCourses.length || moduleAcknowledging}
          >
            <FiCheckCircle /> Acknowledge shown
          </button>
        </div>

        {error && <p className="new-hrm-empty-state new-hrm-learning-alert">{error}</p>}
        {message && <p className="new-hrm-empty-state new-hrm-learning-alert new-hrm-learning-alert--success">{message}</p>}

        <header className="new-hrm-panel__header new-hrm-panel__header--compact new-hrm-learning-library-heading">
          <div>
            <h2>Training Library</h2>
            <p>{groupedCourses.length} categories · {visibleCourses.length} modules shown</p>
          </div>
        </header>
        <div className="new-hrm-learning-sections">
          {!visibleCourses.length && !loading && (
            <p className="new-hrm-empty-state">No active training assignments returned for this employee.</p>
          )}
          {groupedCourses.map((group, groupIndex) => {
            const categoryExpanded = Boolean(debouncedSearchTerm) || expandedCategories.has(group.category);
            const categoryPanelId = `training-category-${groupIndex}`;
            return (
              <section key={group.category} className="new-hrm-learning-section" aria-label={`${group.category} training`}>
                <button
                  className="new-hrm-learning-section__heading"
                  type="button"
                  aria-controls={categoryPanelId}
                  aria-expanded={categoryExpanded}
                  onClick={() => toggleCategory(group.category)}
                >
                  <strong>{group.category}</strong>
                  <span>{group.courses.length} {group.courses.length === 1 ? "module" : "modules"} · {formatTrainingMaxTime(group.maxTimeHours)}</span>
                  <em>{group.pendingCount ? `${group.pendingCount} pending` : "Clear"}</em>
                  <FiChevronDown aria-hidden="true" />
                </button>
                {categoryExpanded && (
                  <div className="new-hrm-learning-section__rows" id={categoryPanelId}>
                    {group.courses.map((course) => {
                      const courseKey = getCourseKey(course);
                      const selected = expandedCourseId === courseKey;
                      return (
                        <React.Fragment key={courseKey}>
                          <button
                            className={`new-hrm-learning-row new-hrm-learning-row--flat${selected ? " new-hrm-learning-row--active" : ""}`}
                            type="button"
                            aria-controls={selected ? `training-detail-${courseKey}` : undefined}
                            aria-expanded={selected}
                            onClick={() => {
                              if (selected) {
                                setExpandedCourseId("");
                                return;
                              }
                              setExpandedCourseId(courseKey);
                              loadCourseDetail(course);
                            }}
                          >
                            <span className={`new-hrm-learning-row__mark new-hrm-learning-status--${getStatusTone(course)}`}>
                              {course.acknowledged ? <FiCheckCircle aria-hidden="true" /> : <FiFileText aria-hidden="true" />}
                            </span>
                            <span>
                              <strong>{course.title}</strong>
                              <small>{course.provider || "Antrazal Training"}</small>
                            </span>
                            <span className="new-hrm-learning-row__meta">
                              <small>{formatTrainingMaxTime(getCourseMaxTimeHours(course))}</small>
                              <small>{course.assignedDate || "Assigned"}</small>
                              <small>{course.mentorApprovalStatus || "Pending Review"}</small>
                            </span>
                            <em className={`new-hrm-learning-status new-hrm-learning-status--${getStatusTone(course)}`}>
                              {course.acknowledged ? "Acknowledged" : course.status}
                            </em>
                          </button>
                          {selected && (
                            <div className="new-hrm-learning-inline-detail" id={`training-detail-${courseKey}`}>
                              {renderSelectedCourseDetail()}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </section>

      {moduleAckDialog && (
        <div className="new-hrm-modal-backdrop" role="presentation">
          <section className="new-hrm-modal new-hrm-learning-bulk-modal new-hrm-loader-host" role="dialog" aria-modal="true" aria-labelledby="module-ack-title">
            <ComponentLoader
              active={moduleAcknowledging}
              label={`Acknowledging ${moduleAckProgress.completed}/${moduleAckProgress.total}`}
            />
            <header className="new-hrm-modal__header">
              <div>
                <p className="new-hrm-modal-note">Training acknowledgement</p>
                <h2 id="module-ack-title">Acknowledge {moduleAckDialog.title}</h2>
                <p>
                  The same proof will be saved against {moduleAckDialog.courses.length} pending training {moduleAckDialog.courses.length === 1 ? "assignment" : "assignments"}.
                </p>
              </div>
              <button className="new-hrm-icon-button" type="button" onClick={closeModuleAcknowledgement} disabled={moduleAcknowledging} aria-label="Close acknowledgement dialog">
                <FiX />
              </button>
            </header>

            <div className="new-hrm-learning-bulk-modal__summary">
              <span><strong>{moduleAckDialog.courses.length}</strong><small>pending assignments</small></span>
              <span><strong>{moduleAckProgress.completed}/{moduleAckProgress.total}</strong><small>processed</small></span>
            </div>

            {moduleAckError && <p className="new-hrm-learning-alert new-hrm-learning-bulk-modal__error">{moduleAckError}</p>}

            <div className="new-hrm-learning-bulk-modal__list" aria-label="Training assignments to acknowledge">
              {moduleAckDialog.courses.slice(0, 8).map((course) => (
                <span key={getCourseKey(course)}>
                  <FiFileText />
                  <strong>{course.title}</strong>
                </span>
              ))}
              {moduleAckDialog.courses.length > 8 && (
                <em>+{moduleAckDialog.courses.length - 8} more assignments</em>
              )}
            </div>

            <section className="new-hrm-learning-proof new-hrm-learning-bulk-modal__form">
              <div>
                <h3>Proof for Mentor Review</h3>
                <p>Add proof text or a proof link. CRM requires one of them before any training can be acknowledged.</p>
              </div>
              <label className="new-hrm-learning-proof__full">
                <span>Proof</span>
                <textarea
                  rows={5}
                  value={moduleAckForm.proof}
                  onChange={(event) => setModuleAckForm((current) => ({ ...current, proof: event.target.value }))}
                  placeholder="Summarize the completed module work."
                />
              </label>
              <label>
                <span>Proof URL</span>
                <input
                  type="url"
                  value={moduleAckForm.proofUrl}
                  onChange={(event) => setModuleAckForm((current) => ({ ...current, proofUrl: event.target.value }))}
                  placeholder="https://..."
                />
              </label>
              <label>
                <span>Comments for Mentor</span>
                <textarea
                  rows={3}
                  value={moduleAckForm.employeeComments}
                  onChange={(event) => setModuleAckForm((current) => ({ ...current, employeeComments: event.target.value }))}
                  placeholder="Optional notes for mentor review."
                />
              </label>
            </section>

            <div className="new-hrm-learning-bulk-modal__actions">
              <button className="new-hrm-secondary-button" type="button" onClick={closeModuleAcknowledgement} disabled={moduleAcknowledging}>
                Cancel
              </button>
              <button className="new-hrm-primary-button" type="button" onClick={handleAcknowledgeModule} disabled={moduleAcknowledging || !moduleAckProofReady}>
                <FiCheckCircle /> Acknowledge module
              </button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
};

const TrainingScreen = (props) => {
  const [tab, setTab] = useState("library");
  const selectWithKeyboard = (event) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const next = event.key === "Home" ? "library" : event.key === "End" ? "tests" : tab === "library" ? "tests" : "library";
    setTab(next);
    document.getElementById(`training-tab-${next}`)?.focus();
  };
  return <>
    <div className="training-tabs" role="tablist" aria-label="Training views" onKeyDown={selectWithKeyboard}>
      {[['library', 'Learning'], ['tests', 'Tests']].map(([id, label]) => <button type="button" key={id} id={`training-tab-${id}`} role="tab" aria-selected={tab === id} aria-controls={`training-panel-${id}`} tabIndex={tab === id ? 0 : -1} onClick={() => setTab(id)}>{label}</button>)}
    </div>
    <div role="tabpanel" id={`training-panel-${tab}`} aria-labelledby={`training-tab-${tab}`}>
      {tab === "library" ? <LearningScreen {...props} /> : <TrainingTests />}
    </div>
  </>;
};

export default TrainingScreen;
